import { getSession } from 'next-auth/react';

/**
 * Thrown when a client-side token refresh fails.
 *
 * `terminal` is the single source of truth for whether the caller should log the user
 * out: `true` only for an explicit 401 (invalid/revoked refresh token). Network errors,
 * timeouts, and 503s are `terminal: false` so the app keeps the session and retries once
 * connectivity returns.
 */
export class RefreshError extends Error {
  terminal: boolean;

  constructor(message: string, terminal: boolean) {
    super(message);
    // Restore the prototype chain so `instanceof RefreshError` works even when this
    // downlevels to ES5 (extending built-ins otherwise breaks instanceof). Without it
    // isTerminalRefreshError could misread a terminal 401 as transient and never sign out.
    Object.setPrototypeOf(this, RefreshError.prototype);
    this.name = 'RefreshError';
    this.terminal = terminal;
  }
}

/** True only for a `RefreshError` explicitly marked terminal. Any other value (a plain
 *  Error, a network failure, undefined) defaults to non-terminal — we never log a user
 *  out on an ambiguous failure. */
export function isTerminalRefreshError(error: unknown): boolean {
  return error instanceof RefreshError && error.terminal;
}

/**
 * Refresh the Curipod id_token for the current session and return the fresh token.
 *
 * Sessions refresh through NextAuth itself: `getSession()` runs the jwt callback,
 * which calls the backend AND — because this is a browser-originated
 * `/api/auth/session` request — persists the refreshed token back to the session
 * cookie. That cookie write is the crucial part: a custom API route cannot update
 * the browser cookie, so a refresh performed there is orphaned and every later
 * reader is left with the stale token. Routing every client refresh (REST 401s,
 * game-socket reconnects) through here keeps one code path that both refreshes and
 * persists, so the refreshers can't diverge from what the cookie actually holds.
 *
 * @throws {RefreshError} if the refresh failed. `terminal` distinguishes an explicit 401
 *   (revoked token → log out) from a transient failure (offline/timeout/503 → keep session).
 */
export async function refreshClientIdToken(): Promise<string> {
  let session: Awaited<ReturnType<typeof getSession>>;
  try {
    session = await getSession();
  } catch (error) {
    // getSession() failed to reach /api/auth/session (offline) — transient.
    throw new RefreshError(
      error instanceof Error ? error.message : 'getSession failed',
      false,
    );
  }
  // The jwt callback only stamps RefreshAccessTokenError on a genuine 401, so an
  // errored session is terminal. A missing token WITHOUT that error is a transient
  // hiccup (e.g. offline), so we keep the session and let it retry.
  if (session?.error === 'RefreshAccessTokenError') {
    throw new RefreshError('RefreshAccessTokenError', true);
  }
  if (!session?.idToken) {
    throw new RefreshError('token refresh failed', false);
  }
  // A transient server-side refresh failure leaves the OLD (expired) idToken in the
  // session with no error. Returning it would falsely look like a successful refresh
  // and let the caller retry with a stale token, so treat a still-expired token as a
  // transient failure — the refresh will be retried once the backend recovers.
  if (typeof session.idTokenExp === 'number' && session.idTokenExp <= Date.now()) {
    throw new RefreshError('token still expired after refresh', false);
  }
  return session.idToken as string;
}
